import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Paper,
  Typography,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';

interface LocationData {
  addressDetails: {
    formattedAddress: string;
    id: string;
    lat: number;
    lon: number;
  };
  id: string;
  siteRestriction: {
    serviceabilityStatus: string;
    supportingTechnology: {
      primaryAccessTechnology: string;
      serviceabilityClass: string;
      businessFibre: boolean;
    };
    supportingRelatedLocationFeatures: {
      buildingType: string;
      occupancyType: string;
    };
  };
}

/** Strip leading [A-Z]+. prefix from a location ID (e.g. "PA." or "LNTL."). */
export const cleanLocationId = (id: string) => id.replace(/^[A-Z]+\./, '');

const Field = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <Box sx={{ display: 'flex', gap: 2, py: 0.75 }}>
    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 200 }}>
      {label}
    </Typography>
    <Typography variant="body2">{value}</Typography>
  </Box>
);

const LocationPage = () => {
  const { locationId } = useParams<{ locationId: string }>();
  const navigate = useNavigate();

  const [data, setData] = useState<LocationData | null>(null);
  const [status, setStatus] = useState<'loading' | 'failed' | 'idle'>('loading');

  useEffect(() => {
    if (!locationId) return;
    const cleaned = cleanLocationId(locationId);
    setStatus('loading');
    fetch(`https://api.public.dgtek.net/location/${cleaned}`)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json() as Promise<LocationData>;
      })
      .then((d) => {
        setData(d);
        setStatus('idle');
      })
      .catch(() => setStatus('failed'));
  }, [locationId]);

  const cleaned = locationId ? cleanLocationId(locationId) : '';

  return (
    <Box sx={{ mx: 15, mt: 10 }}>
      <Button
        startIcon={<ArrowBack />}
        onClick={() => navigate(-1)}
        sx={{ mb: 2 }}
      >
        Back
      </Button>

      <Paper elevation={0} sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Location:
          {' '}
          {cleaned}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {locationId}
        </Typography>

        <Divider sx={{ mb: 2 }} />

        {status === 'loading' && <CircularProgress size={24} />}
        {status === 'failed' && (
          <Typography color="error">Failed to load location data.</Typography>
        )}
        {status === 'idle' && data && (
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Address</Typography>
            <Field label="Formatted Address" value={data.addressDetails.formattedAddress} />
            <Field
              label="Coordinates"
              value={`${data.addressDetails.lat}, ${data.addressDetails.lon}`}
            />

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle2" sx={{ mb: 1 }}>Serviceability</Typography>
            <Field label="Status" value={data.siteRestriction.serviceabilityStatus} />
            <Field
              label="Primary Access Technology"
              value={data.siteRestriction.supportingTechnology.primaryAccessTechnology}
            />
            <Field
              label="Serviceability Class"
              value={data.siteRestriction.supportingTechnology.serviceabilityClass}
            />
            <Field
              label="Building Type"
              value={data.siteRestriction.supportingRelatedLocationFeatures.buildingType}
            />
            <Field
              label="Occupancy Type"
              value={data.siteRestriction.supportingRelatedLocationFeatures.occupancyType}
            />
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default LocationPage;
